import { watch } from 'vue';
import { child, set } from 'firebase/database';
import AdapterMobileHistoryV2 from '~/class/AdapterMobileHistoryV2';

// mobile-v2 per-table HISTORY publisher.
//
//   customer/{storeId}/mobile_v2_history/{alias}/{sanitizedJobId} = { ...record }
//
// Read by the Android table-detail History tab (tab4). Keyed by the table's real
// name ("T7") — the legacy node it replaces is keyed by a 1-based counter that
// runs across all zones, so adding/renaming a zone renumbers every table and the
// phone starts reading someone else's history.
//
// Deliberately a SEPARATE plugin from mobile-v1.sync.plugin.ts, which is NOT
// modified: that plugin's publish path is entangled with a 600ms timer and the
// `allowEmptyPublish` stale-read guard, and none of that complexity applies here.
// This plugin only ever writes one child per ADD/VOID — on an idempotent, jobId-derived
// key, so a repeated add() can never duplicate a row — and nulls a table's node when the
// table leaves the jobchain. Both are timing-independent.
//
// Scope, matching the legacy silo: ADD + VOID only. Unlike the legacy silo,
// phone-originated ADDs (source === 'm') ARE included — mobile-v2 is now the only
// phone transport and it writes no history of its own, so without this the
// phone's own orders are missing from its History tab entirely.
//
// Lifetime: the current table session. The node is cleared when the table leaves
// jobStore.tables (paid in full / cleared / moved) and at close-shop
// (pos.shop.ts clearCloudData).
export default defineNuxtPlugin(() => {

    const bootstrapStore = useBootstrapStore()
    const shopStore = usePosShopStore()
    const jobStore = usePosJobsStore()
    const rtdb = useRealtimeDatabase()

    // Gated at write time (not plugin-init time) because runMode loads later in
    // bootstrap, which also handles a mid-session mode switch — same idiom as
    // mobile-v1.sync.plugin.ts's isMainCashier().
    //
    // The 2nd cashier is allowed here, unlike the legacy plugin: each device only
    // sees its own add() via $onAction, so there is no race — a 2nd-cashier order
    // simply appears in history, which today it never does. The legacy plugin's
    // publish/clear paths must stay main-cashier-only (they overwrite whole-table
    // state), and this plugin never touches them.
    const isActive = () =>
        bootstrapStore.loaded &&
        (shopStore.runMode === "cashier" || shopStore.runMode === "2nd_cashier")

    // Table keys present on the last compute, so we can detect the ones that
    // disappeared. Starts empty on every page load, so a reload can never
    // fabricate a removal and wipe a live table's history.
    let _previousTableKeys = new Set<string>()

    jobStore.$onAction(({ name, args, after }) => {
        if (name !== "add") return
        const job = args[0]
        after(() => {
            // Runs synchronously inside the order-entry path — a missing history
            // row must never be able to break ringing up an order.
            try {
                writeHistoryRecord(job)
            } catch (e) {
                console.warn('[mobile-v2-history] write failed', e)
            }
        })
    })

    watch(() => jobStore.tables, () => {
        try {
            reconcileRemovedTables()
        } catch (e) {
            console.warn('[mobile-v2-history] reconcile failed', e)
        }
    })

    function historyRoot() {
        if (!isActive()) return null
        // "_" is the pre-bootstrap default storeId — writing there would litter a
        // junk node shared by every store.
        if (!shopStore.storeId || String(shopStore.storeId) === "_") return null
        return rtdb.mobileV2HistoryRef()
    }

    function writeHistoryRecord(job: any) {
        const root = historyRoot()
        if (!root) return

        const { alias, key, record } = new AdapterMobileHistoryV2().fromJobToHistory(job)
        if (!alias || !key || !record) return

        // set() on a DETERMINISTIC key (the sanitized jobId, same derivation the
        // jobchain uses for its own node), NOT push(): the write must be idempotent.
        // $onAction("add") can fire more than once for the same card — a retry, a
        // replay, an offline flush — and with push() each one would add ANOTHER
        // history row for an order that was only rung up once. Overwriting one node
        // is self-healing; a duplicate row is not.
        //
        // Ordering is unaffected: jobId is "<ISO timestamp>__<alias>", so these keys
        // sort chronologically just like push keys did.
        //
        // Raw object (NOT a JSON string like the legacy node) — the Android mapper
        // walks the Map directly.
        set(child(child(root, alias), key), record)
    }

    // Clear the history node of any table that left the jobchain since the last
    // compute (PAY in full / CLEAR / MOVE_ALL). Driving this off the actual table
    // state is authoritative and timing-independent — there is no publish timer to
    // race, so the legacy plugin's allowEmptyPublish guard has no analogue here.
    //
    // These keys come straight from jobStore.tables, so they are ALREADY
    // normalized — do not re-normalize them.
    function reconcileRemovedTables() {
        const currentKeys = new Set<string>(Object.keys(jobStore.tables || {}))
        const root = historyRoot()
        if (root) {
            for (const key of _previousTableKeys) {
                if (currentKeys.has(key)) continue
                // set(ref, null), never set(ref, []) — an empty array is equivalent
                // to null in the JS SDK, and the string "[]" would leave a leaf
                // where the phone expects children.
                set(child(root, key), null)
            }
        }
        _previousTableKeys = currentKeys
    }
})
